import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload, CreditCard, CheckCircle, Wallet, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BAddCredit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [activeTab, setActiveTab] = useState('ziina');
  
  // Ziina payment form
  const [topupAmount, setTopupAmount] = useState('');
  
  // Receipt upload form
  const [receiptData, setReceiptData] = useState({
    amount: '',
    notes: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agent_token');
    if (!token) {
      navigate('/b2b-agent');
      return;
    }
    fetchBalance();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
  });

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/b2b/wallet/balance`, getAuthHeader());
      setWalletBalance(res.data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  // Ziina payment handler
  const handleZiinaTopup = async (e) => {
    e.preventDefault();
    
    const amount = parseFloat(topupAmount);
    if (!amount || amount < 100) {
      toast.error('Minimum top-up amount is 100 AED');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('amount', amount);

      const response = await axios.post(
        `${API}/b2b/wallet/topup`,
        formData,
        getAuthHeader()
      );

      if (response.data.redirect_url) {
        // Redirect to Ziina payment page
        window.location.href = response.data.redirect_url;
      } else {
        toast.error('Failed to initialize payment');
      }
    } catch (error) {
      console.error('Error creating top-up:', error);
      toast.error(error.response?.data?.detail || 'Failed to create top-up');
    } finally {
      setLoading(false);
    }
  };

  // Receipt upload handler
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should be less than 10MB');
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    
    if (!receiptData.amount || parseFloat(receiptData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!receiptFile) {
      toast.error('Please upload payment receipt');
      return;
    }

    setLoading(true);
    
    try {
      const submitData = new FormData();
      submitData.append('amount', receiptData.amount);
      submitData.append('notes', receiptData.notes);
      submitData.append('receipt', receiptFile);

      await axios.post(
        `${API}/b2b/credit-requests`,
        submitData,
        {
          ...getAuthHeader(),
          headers: {
            ...getAuthHeader().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Credit request submitted! Admin will review and approve.');
      navigate('/b2b-agent/dashboard');
    } catch (error) {
      console.error('Error submitting credit request:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit credit request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="b2b-add-credit-page">
      {/* Header */}
      <header className="bg-[#1A4D7F] text-white">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                onClick={() => navigate('/b2b-agent/dashboard')}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold">Add Credit</h1>
              </div>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-2">
              <p className="text-xs text-blue-100">Current Balance</p>
              <p className="text-lg font-bold">{walletBalance.toFixed(2)} AED</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-2xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 h-12">
            <TabsTrigger value="ziina" className="text-base">
              <CreditCard className="w-4 h-4 mr-2" />
              Pay with Ziina
            </TabsTrigger>
            <TabsTrigger value="receipt" className="text-base">
              <Receipt className="w-4 h-4 mr-2" />
              Upload Receipt
            </TabsTrigger>
          </TabsList>

          {/* Ziina Payment Tab */}
          <TabsContent value="ziina">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-[#009FB7]" />
                  Instant Top-up with Ziina
                </CardTitle>
                <CardDescription>
                  Pay securely with your card. Credit will be added instantly after payment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleZiinaTopup} className="space-y-6">
                  <div>
                    <Label htmlFor="topup-amount">Amount (AED) *</Label>
                    <Input
                      id="topup-amount"
                      type="number"
                      min="100"
                      step="0.01"
                      placeholder="Enter amount (min 100 AED)"
                      value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      className="mt-2 h-12 text-lg"
                      data-testid="ziina-amount-input"
                    />
                    <p className="text-sm text-slate-500 mt-1">Minimum: 100 AED</p>
                  </div>

                  {/* Quick amount buttons */}
                  <div className="grid grid-cols-4 gap-2">
                    {[500, 1000, 2000, 5000].map(amt => (
                      <Button
                        key={amt}
                        type="button"
                        variant="outline"
                        onClick={() => setTopupAmount(amt.toString())}
                        className={topupAmount === amt.toString() ? 'border-[#009FB7] bg-[#009FB7]/10' : ''}
                      >
                        {amt} AED
                      </Button>
                    ))}
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Instant Credit</span>
                    </div>
                    <p className="text-sm text-green-700 mt-1">
                      Your wallet will be credited immediately after successful payment.
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#009FB7] hover:bg-[#008fa5] h-12 text-lg"
                    data-testid="ziina-pay-button"
                  >
                    {loading ? 'Processing...' : `Pay ${topupAmount || '0'} AED with Ziina`}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Receipt Upload Tab */}
          <TabsContent value="receipt">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="w-6 h-6 text-[#009FB7]" />
                  Bank Transfer / Receipt Upload
                </CardTitle>
                <CardDescription>
                  Upload your payment receipt. Admin will verify and add credit within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReceiptSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="receipt-amount">Amount (AED) *</Label>
                    <Input
                      id="receipt-amount"
                      type="number"
                      min="100"
                      step="0.01"
                      placeholder="Enter amount"
                      value={receiptData.amount}
                      onChange={(e) => setReceiptData({ ...receiptData, amount: e.target.value })}
                      className="mt-2"
                      data-testid="receipt-amount-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="receipt-file">Payment Receipt *</Label>
                    <div className="mt-2 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#009FB7] transition-colors">
                      <input
                        id="receipt-file"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        data-testid="receipt-file-input"
                      />
                      <label htmlFor="receipt-file" className="cursor-pointer">
                        {receiptFile ? (
                          <div className="flex items-center justify-center gap-2 text-green-600">
                            <CheckCircle className="w-6 h-6" />
                            <span className="font-medium">{receiptFile.name}</span>
                          </div>
                        ) : (
                          <div>
                            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                            <p className="text-slate-600">Click to upload receipt</p>
                            <p className="text-sm text-slate-400">PNG, JPG, or PDF up to 10MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="receipt-notes">Notes (Optional)</Label>
                    <Textarea
                      id="receipt-notes"
                      placeholder="Bank transfer reference, transaction ID, etc."
                      value={receiptData.notes}
                      onChange={(e) => setReceiptData({ ...receiptData, notes: e.target.value })}
                      className="mt-2"
                      rows={3}
                      data-testid="receipt-notes-input"
                    />
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Bank Transfer Details</h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p><strong>Bank:</strong> ADIB</p>
                      <p><strong>Account Name:</strong> Aone Tourism LLC</p>
                      <p><strong>Account Number:</strong> 19242268</p>
                      <p><strong>IBAN:</strong> AE530500000000019242268</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1A4D7F] hover:bg-[#153e66] h-12"
                    data-testid="submit-receipt-button"
                  >
                    {loading ? 'Submitting...' : 'Submit Credit Request'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default B2BAddCredit;
